import { useMemo } from "react";

import { MetricTile } from "./EvalCharts";
import { WerChartRenderer } from "./WerCharts";

const METRIC_GROUPS = [
  { id: "corpus_wer", title: "Corpus WER", match: (m) => m.id?.startsWith("corpus_wer_") && !m.id.startsWith("corpus_llm_wer_") },
  { id: "regressions", title: "Regression counts", match: (m) => m.id?.startsWith("regressions_") },
  { id: "corpus_llm_wer", title: "Corpus LLM WER", match: (m) => m.id?.startsWith("corpus_llm_wer_") },
];

function groupMetrics(metrics) {
  const groups = METRIC_GROUPS.map((g) => ({ ...g, items: [] }));
  const otherKey = { id: "other", title: "Other", items: [] };
  const headlineKey = { id: "headline", title: "Headline", items: [] };

  metrics.forEach((m) => {
    if (m.id === "n_rows" || m.id === "wer_n_rows") {
      headlineKey.items.push(m);
      return;
    }
    const g = groups.find((g) => g.match(m));
    if (g) g.items.push(m);
    else otherKey.items.push(m);
  });

  return [headlineKey, ...groups, otherKey].filter((g) => g.items.length > 0);
}

function findChart(charts, id) {
  return (charts || []).find((c) => c?.id === id);
}

function ChartSlot({ chart, wide = false }) {
  if (!chart) return null;
  return <WerChartRenderer chart={{ ...chart, _wide: wide }} />;
}

export default function WerSection({ section }) {
  const charts = section.charts || [];
  const grouped = useMemo(() => groupMetrics(section.metrics || []), [section.metrics]);

  const headline = findChart(charts, "corpus_wer_matrix");
  const ageCombo = findChart(charts, "wer_by_age_combo");
  const bestByAge = findChart(charts, "best_combo_by_age");
  const byAudioLabel = findChart(charts, "wer_by_audio_label");
  const byQualityFlag = findChart(charts, "wer_by_quality_flag");
  const buckets = findChart(charts, "wer_buckets");
  const regressions = findChart(charts, "regressions");
  const llmWer = findChart(charts, "corpus_llm_wer");
  const llmDelta = findChart(charts, "llm_wer_vs_standard");

  const knownIds = new Set([
    "corpus_wer_matrix", "wer_by_age_combo", "best_combo_by_age",
    "wer_by_audio_label", "wer_by_quality_flag", "wer_buckets",
    "regressions", "corpus_llm_wer", "llm_wer_vs_standard",
  ]);
  const extras = charts.filter((c) => c && !knownIds.has(c.id));

  return (
    <div className="eval-wer-section">
      {section.description && (
        <p className="eval-section-caption">{section.description}</p>
      )}

      {grouped.length > 0 && (
        <div className="eval-wer-metrics">
          {grouped.map((g) => (
            <div key={g.id} className="eval-wer-metric-group">
              <h4 className="eval-wer-metric-group-title">{g.title}</h4>
              <div className="eval-metrics-grid">
                {g.items.map((m) => (
                  <MetricTile key={m.id} metric={m} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {headline && (
        <div className="eval-wer-headline">
          <ChartSlot chart={headline} wide={true} />
        </div>
      )}

      {(ageCombo || bestByAge) && (
        <div className="eval-wer-row two-col">
          <ChartSlot chart={ageCombo} />
          <ChartSlot chart={bestByAge} />
        </div>
      )}

      {(byAudioLabel || byQualityFlag) && (
        <div className="eval-wer-row two-col">
          <ChartSlot chart={byAudioLabel} />
          <ChartSlot chart={byQualityFlag} />
        </div>
      )}

      {buckets && (
        <div className="eval-wer-row">
          <ChartSlot chart={buckets} wide={true} />
        </div>
      )}

      {regressions && (
        <div className="eval-wer-row">
          <ChartSlot chart={regressions} wide={true} />
        </div>
      )}

      {(llmWer || llmDelta) && (
        <div className="eval-wer-row two-col">
          <ChartSlot chart={llmWer} />
          <ChartSlot chart={llmDelta} />
        </div>
      )}

      {extras.length > 0 && (
        <div className="eval-wer-row">
          {extras.map((c) => (
            <ChartSlot key={c.id} chart={c} wide={true} />
          ))}
        </div>
      )}
    </div>
  );
}
